import {ReactNode} from 'react'

import CheckIcon from '@mui/icons-material/Check'
import CloseIcon from '@mui/icons-material/Close'
import {
  Box,
  Drawer,
  IconButton,
  List,
  ListItemButton,
  Theme,
} from '@mui/material'
import {PGTypography} from '@progynyglobal/pg-ui-library'
import {makeStyles} from 'tss-react/mui'

export type BottomSheetPickerItem = {
  key: string
  label: ReactNode
  selected: boolean
}

type BottomSheetPickerProps = {
  open: boolean
  onClose?: () => void
  title: ReactNode
  items: BottomSheetPickerItem[]
  onSelect: (key: string) => void
}

const BottomSheetPicker = ({
  open,
  onClose,
  title,
  items,
  onSelect,
}: BottomSheetPickerProps) => {
  const {classes} = useStyles()

  return (
    <Drawer
      anchor='bottom'
      open={open}
      onClose={onClose}
      PaperProps={{className: classes.paper}}
      BackdropProps={{sx: {backgroundColor: 'rgba(0, 0, 0, 0.4)'}}}
    >
      <Box className={classes.handle} />
      <Box className={classes.header}>
        <PGTypography variant='title' className={classes.title}>
          {title}
        </PGTypography>
        <IconButton onClick={onClose} className={classes.closeButton}>
          <CloseIcon fontSize='small' />
        </IconButton>
      </Box>
      <List className={classes.list}>
        {items.map(({key, label, selected}) => (
          <ListItemButton
            key={key}
            selected={selected}
            onClick={() => onSelect(key)}
            className={classes.row}
            classes={{selected: classes.rowSelected}}
          >
            <PGTypography variant='body' className={classes.rowLabel}>
              {label}
            </PGTypography>
            {selected && <CheckIcon className={classes.checkIcon} />}
          </ListItemButton>
        ))}
      </List>
    </Drawer>
  )
}

export default BottomSheetPicker

const useStyles = makeStyles()((theme: Theme) => ({
  paper: {
    borderTopLeftRadius: 36,
    borderTopRightRadius: 36,
    maxHeight: '55vh',
    display: 'flex',
    flexDirection: 'column',
  },
  handle: {
    width: 54,
    height: 5,
    borderRadius: 999,
    backgroundColor: '#D6D8E0',
    margin: `${theme.spacing(1)} auto`,
    flexShrink: 0,
  },
  header: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    padding: theme.spacing(2, 3),
    flexShrink: 0,
  },
  title: {
    color: theme.palette.text.primary,
  },
  closeButton: {
    backgroundColor: theme.palette.other.brandColors.navColor,
  },
  list: {
    overflowY: 'auto',
    flex: '1 1 auto',
    padding: 0,
  },
  row: {
    minHeight: 50,
    paddingLeft: theme.spacing(3),
    paddingRight: theme.spacing(3),
    color: theme.palette.text.primary,
  },
  rowSelected: {
    backgroundColor: `${theme.palette.tertiary.main} !important`,
    color: theme.palette.tertiary.contrastText,
  },
  rowLabel: {
    flexGrow: 1,
  },
  checkIcon: {
    color: theme.palette.tertiary.contrastText,
    fontSize: 20,
  },
}))
